import type { ArticleDetail, ArticleInput, ArticleKind } from "./types";

export const LIMITS = { title: 160, summary: 300, body: 20_000, cover: 2000, tags: 5, tag: 30 } as const;

/** What the editor holds while typing. Tags stay raw text so a half-typed comma is not eaten. */
export interface ArticleFormValues {
  title: string;
  summary: string;
  kind: ArticleKind;
  tags: string;
  relatedAssessmentCode: string;
  coverImageUrl: string;
  bodySource: string;
  isPlaceholder: boolean;
}

export type FormErrors = Partial<Record<keyof ArticleFormValues, string>>;

export const emptyForm: ArticleFormValues = {
  title: "",
  summary: "",
  kind: "article",
  tags: "",
  relatedAssessmentCode: "",
  coverImageUrl: "",
  bodySource: "",
  isPlaceholder: false,
};

/** The "placeholder" box starts unchecked: saving as an admin turns the flag off unless asked otherwise. */
export function toForm(a: ArticleDetail): ArticleFormValues {
  return {
    title: a.title,
    summary: a.summary,
    kind: a.kind,
    tags: a.tags.join(", "),
    relatedAssessmentCode: a.relatedAssessmentCode ?? "",
    coverImageUrl: a.coverImageUrl ?? "",
    bodySource: a.bodySource,
    isPlaceholder: false,
  };
}

export function parseTags(raw: string): string[] {
  return [...new Set(raw.split(",").map((t) => t.trim().toLowerCase()).filter(Boolean))];
}

export function toInput(v: ArticleFormValues): ArticleInput {
  return {
    title: v.title.trim(),
    summary: v.summary.trim(),
    kind: v.kind,
    bodySource: v.bodySource,
    coverImageUrl: v.coverImageUrl.trim() || null,
    tags: parseTags(v.tags),
    relatedAssessmentCode: v.relatedAssessmentCode || null,
    isPlaceholder: v.isPlaceholder,
  };
}

export function isHttpsUrl(value: string): boolean {
  try {
    return new URL(value).protocol === "https:";
  } catch {
    return false;
  }
}

export function validateForm(v: ArticleFormValues): FormErrors {
  const errors: FormErrors = {};
  const title = v.title.trim();
  const summary = v.summary.trim();
  if (!title) errors.title = "Judul wajib diisi.";
  else if (title.length > LIMITS.title) errors.title = `Judul maksimal ${LIMITS.title} karakter.`;

  if (!summary) errors.summary = "Ringkasan wajib diisi.";
  else if (summary.length > LIMITS.summary) errors.summary = `Ringkasan maksimal ${LIMITS.summary} karakter.`;

  if (v.bodySource.length > LIMITS.body) errors.bodySource = "Isi artikel maksimal 20.000 karakter.";

  const cover = v.coverImageUrl.trim();
  if (cover.length > LIMITS.cover) errors.coverImageUrl = "URL gambar maksimal 2.000 karakter.";
  else if (cover && !isHttpsUrl(cover)) errors.coverImageUrl = "URL gambar harus diawali https://";

  const tags = parseTags(v.tags);
  if (tags.length > LIMITS.tags) errors.tags = `Maksimal ${LIMITS.tags} tag.`;
  else if (tags.some((t) => t.length > LIMITS.tag)) errors.tags = `Setiap tag maksimal ${LIMITS.tag} karakter.`;
  return errors;
}

/** Compares what would be sent, so spacing and tag case do not count as an edit. */
export function isDirty(form: ArticleFormValues, baseline: ArticleFormValues): boolean {
  return JSON.stringify(toInput(form)) !== JSON.stringify(toInput(baseline));
}
